/**
 * Project tree: the positions in a project (CLAUDE.md 7 Phase A1.2, Appendix A).
 *
 * The FRILO-style project file: Position 1: Beam B1, Position 2: Column C2. Each row
 * shows the short code, the status and the governing utilisation, because those three
 * are what an engineer scans for when returning to a job.
 *
 * The module picker only offers modules the registry says are available, and shows the
 * verification state plainly. An unverified module is more dangerous than an absent
 * one, because it looks exactly like a verified one (section 17).
 */
import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FileStack, Plus, Share2, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useCreatePosition, useModules, usePositions, useProject } from '@/api/hooks'
import { ShareDialog } from '@/components/layout/ShareDialog'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { ProjectSharingDialog } from '@/components/workspace/ProjectSharingDialog'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge, type Status } from '@/components/ui/StatusBadge'
import { useToast } from '@/components/ui/toast'
import { formatUtilisation } from '@/lib/format'
import { PositionRowActions } from '@/components/calc/PositionRowActions'
import { VerificationBadge } from '@/components/calc/VerificationBadge'

/** Position status -> the badge's vocabulary. */
function badgeStatus(status: string): Status {
  if (status === 'pass') return 'pass'
  if (status === 'fail') return 'fail'
  if (status === 'stale' || status === 'error') return 'warning'
  return 'not_applicable'
}

export function ProjectPage() {
  const { t } = useTranslation()
  const params = useParams()
  const projectId = Number(params.projectId)

  const { data: project, isLoading: projectLoading } = useProject(projectId)
  const { data: positions, isLoading: positionsLoading } = usePositions(projectId)
  const [adding, setAdding] = useState(false)
  const [sharing, setSharing] = useState(false)
  // Internal access -- visibility, colleagues, transfer -- as opposed to `sharing`,
  // which mints read-only links for people with no account (11.3/11.5 vs 11.4).
  const [access, setAccess] = useState(false)

  return (
    <WorkspaceChrome title={project?.name ?? t('nav.projects')}>
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {projectLoading ? (
              <Skeleton className="h-7 w-48" />
            ) : (
              <>
                <h2 className="text-lg font-semibold">{project?.name}</h2>
                <p className="text-sm text-text-muted">
                  {[project?.number, project?.client].filter(Boolean).join(' · ') || '—'}
                  {' · '}
                  {project?.code_default} {project?.param_set_default}
                </p>
              </>
            )}
          </div>

          <div className="flex gap-2">
            {/* Only offered where the API would allow it: minting a link is a
                MANAGE action, so an editor or viewer never sees the control
                (CLAUDE.md 11.4). */}
            {project?.capabilities?.includes('project:manage') && (
              <>
                <Button onClick={() => setAccess(true)}>
                  <Users size={16} aria-hidden />
                  {t('sharing.action')}
                </Button>
                <Button onClick={() => setSharing(true)}>
                  <Share2 size={16} aria-hidden />
                  {t('share.action')}
                </Button>
              </>
            )}
            <Button variant="primary" onClick={() => setAdding(true)}>
              <Plus size={16} aria-hidden />
              {t('position.new')}
            </Button>
          </div>
        </div>

        {positionsLoading && <Skeleton className="h-24 w-full" />}

        {!positionsLoading && (positions?.length ?? 0) === 0 && (
          <Card>
            <CardBody className="py-10 text-center">
              <FileStack size={28} className="mx-auto mb-2 text-text-muted" aria-hidden />
              <p className="font-medium">{t('empty.no_positions')}</p>
              <p className="mt-1 text-sm text-text-muted">{t('empty.no_positions_hint')}</p>
            </CardBody>
          </Card>
        )}

        <ul className="space-y-2">
          {(positions ?? []).map((position) => (
            <li key={position.id}>
              {/* The actions sit beside the link, not inside it: a button nested in
                  an anchor is invalid, and every click on the menu would also
                  navigate. */}
              <Card className="flex items-center gap-2 p-3">
                <Link
                  to={`/positions/${position.id}`}
                  className="flex min-w-0 flex-1 flex-wrap items-center gap-3"
                >
                  <span className="tabular w-10 shrink-0 text-sm text-text-muted">
                    {`P${String(position.seq).padStart(2, '0')}`}
                  </span>
                  <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
                    {position.short_code}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium hover:text-accent">
                    {position.name}
                  </span>

                  {position.stale && (
                    <span className="rounded bg-[var(--status-warn-bg)] px-1.5 py-0.5 text-xs text-[var(--status-warn)]">
                      {t('status.stale')}
                    </span>
                  )}

                  {position.result_summary?.governing_ratio != null && (
                    <span className="tabular text-sm text-text-muted">
                      {formatUtilisation(position.result_summary.governing_ratio)}
                    </span>
                  )}

                  <StatusBadge status={badgeStatus(position.status)} />
                </Link>
                <PositionRowActions projectId={projectId} position={position} />
              </Card>
            </li>
          ))}
        </ul>
      </div>

      <NewPositionDialog projectId={projectId} open={adding} onOpenChange={setAdding} />
      <ShareDialog projectId={projectId} open={sharing} onOpenChange={setSharing} />
      {project && <ProjectSharingDialog project={project} open={access} onOpenChange={setAccess} />}
    </WorkspaceChrome>
  )
}

function NewPositionDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation(['common', 'report'])
  const { toast } = useToast()
  const { data: registry } = useModules()
  const createPosition = useCreatePosition(projectId)

  const [name, setName] = useState('')
  const [moduleId, setModuleId] = useState('')
  const [pending, setPending] = useState(false)

  const modules = registry?.modules ?? []

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || !moduleId) return
    setPending(true)
    try {
      await createPosition.mutateAsync({ name: name.trim(), module_id: moduleId })
      setName('')
      setModuleId('')
      onOpenChange(false)
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('position.new')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field>
            <FieldLabel>{t('common.name')}</FieldLabel>
            <FieldInput
              required
              // Inside a modal that the user just opened deliberately. Moving focus to
              // the first field is the expected behaviour here, not a focus steal --
              // the rule's concern is autoFocus on page load.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('common.module')}</legend>
            {modules.map((module) => (
              <label
                key={module.module_id}
                htmlFor={`module-${module.module_id}`}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-surface-raised"
              >
                <input
                  id={`module-${module.module_id}`}
                  type="radio"
                  name="module"
                  value={module.module_id}
                  checked={moduleId === module.module_id}
                  onChange={() => setModuleId(module.module_id)}
                  className="mt-1"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
                      {module.short_code}
                    </span>
                    <span className="font-medium">{t(module.name_key, { ns: 'report' })}</span>
                    <span className="text-xs text-text-muted">v{module.version}</span>
                  </span>
                  <span className="mt-1 flex flex-wrap gap-1.5">
                    {module.supported_codes.map((code) => (
                      <VerificationBadge
                        key={code}
                        code={code}
                        status={module.verification[code] ?? 'unverified'}
                      />
                    ))}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!moduleId}>
              {t('action.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
